"use client";
import React from "react";
import { useThemeUI } from "theme-ui";

import { ThemeContext } from "./ThemeContext";
import {
  Theme,
  ThemeBorders,
  ThemeColors,
  ThemeFonts,
  ThemeFontSizes,
  ThemeFontWeights,
  ThemeMode,
  ThemeProvided,
  ThemeSizes,
  ThemeSpaces,
  ThemeZIndices
} from "./types";

// We need to enable using the useTheme hook like it returns
// the types required by the Box's sx prop (even though it often
// returns primitive values.) To do this, we create a unique type
// that returns what the sx properties will require.
export interface UseThemeTheme {
  borders: {
    [key in keyof ThemeProvided["borders"]]: ThemeBorders;
  };
  breakpoints: Theme["breakpoints"];
  colors: {
    [key in keyof ThemeProvided["colors"]]: ThemeColors;
  };
  fonts: {
    [key in keyof ThemeProvided["fonts"]]: ThemeFonts;
  };
  fontSizes: {
    [key in keyof ThemeProvided["fontSizes"]]: ThemeFontSizes;
  };
  fontWeights: {
    [key in keyof ThemeProvided["fontWeights"]]: ThemeFontWeights;
  };
  grid: {
    [key in keyof ThemeProvided["grid"]]: ThemeSizes;
  };
  lineHeights: {
    [key in keyof ThemeProvided["lineHeights"]]: ThemeFontSizes;
  };
  mode: ThemeMode;
  space: {
    [key in keyof ThemeProvided["space"]]: ThemeSpaces;
  };
  zIndices: {
    [key in keyof ThemeProvided["zIndices"]]: ThemeZIndices;
  };
  sizes: {
    [key in keyof ThemeProvided["sizes"]]: ThemeSizes;
  };
}

export const useTheme = (): UseThemeTheme => {
  const { mode } = React.useContext(ThemeContext);

  return {
    ...(useThemeUI().theme as unknown as UseThemeTheme),
    mode
  };
};

export const useThemeWithoutTypes = (): ThemeProvided => {
  return useThemeUI().theme as unknown as ThemeProvided;
};
